// crewbit-admin/src/hooks/useTaxSummaryDetail.ts
//
// Hook contract: docs/references/MONEY_MODULE_SPEC_v1.md §Tax.6 (B-N12).
// Returns the structured tax-summary envelope from generate_tax_summary RPC for
// a single (taxType, periodStart, periodEnd) tuple. Read-only.
//
// Mirrors mobile crewbit-mobile/hooks/useTaxSummaryDetail.ts shape.
// Web runtime: Page Visibility API for focus refetch; window event listener
// for MONEY_MUTATION_EVENT (no DeviceEventEmitter equivalent).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { callMoneyRpc } from '../lib/moneyRpc';
import { MONEY_MUTATION_EVENT } from './useFinancialSummary';
import { useActiveBusinessStore } from './useActiveBusiness';
import type { TaxType } from '../lib/taxMutations';

const FOCUS_REFETCH_THROTTLE_MS = 30_000;

export interface TaxSummaryDetailArgs {
  /** ISO date 'YYYY-MM-DD'. Pass null to skip the fetch (returns initial state). */
  periodStart: string | null;
  periodEnd: string | null;
  taxType: TaxType;
}

export interface InvoiceContribution {
  invoice_id: string;
  invoice_number: string;
  client_name: string | null;
  paid_at: string;
  total: number;
  tax_amount: number;
}

export interface ExpenseContribution {
  expense_id: string;
  description: string | null;
  vendor_name: string | null;
  date: string;
  amount: number;
  tax_amount: number;
}

export interface T4AVendorRow {
  vendor_id: string;
  vendor_name: string | null;
  business_number: string | null;
  ytd_total: number;
  bill_count: number;
}

export interface TaxSummaryDetailEnvelope {
  tax_type: TaxType;
  tax_mode: 'charge' | 'exempt' | 'quick_method';
  business_number: string | null;
  period_start: string;
  period_end: string;
  generated_at: string;

  // Box-by-box breakdown (HST/GST/QST/PST paths only — null for t4a)
  box_101_revenue: number | null;
  box_103_zero_rated: number | null;
  box_105_hst_collected: number | null;
  box_106_adjustments: number | null;
  box_108_itcs: number | null;
  box_109_net: number | null;

  // Quick Method (when tax_mode='quick_method')
  quick_method_credit: number | null;
  quick_method_remittance_rate: number | null;
  quick_method_revenue_threshold: number | null;

  invoice_contributions: InvoiceContribution[] | null;
  expense_contributions: ExpenseContribution[] | null;

  // T4A vendor aggregation (when taxType='t4a')
  t4a_vendors: T4AVendorRow[] | null;

  status: 'open' | 'filed' | 'amended' | 'reopened' | 'exempt';
}

export interface UseTaxSummaryDetailReturn {
  data: TaxSummaryDetailEnvelope | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useTaxSummaryDetail({
  periodStart,
  periodEnd,
  taxType,
}: TaxSummaryDetailArgs): UseTaxSummaryDetailReturn {
  const activeBusinessId = useActiveBusinessStore((s) => s.activeBusinessId);

  const [data, setData] = useState<TaxSummaryDetailEnvelope | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const dataRef = useRef<TaxSummaryDetailEnvelope | null>(null);
  useEffect(() => { dataRef.current = data; }, [data]);

  const lastFocusFetchRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const doFetch = useCallback(
    async (manual: boolean) => {
      if (!activeBusinessId || !periodStart || !periodEnd) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const isInitial = dataRef.current === null;
      if (isInitial) setIsLoading(true);
      else if (manual) setIsRefreshing(true);

      try {
        const result = await callMoneyRpc<TaxSummaryDetailEnvelope>(
          'generate_tax_summary',
          {
            p_business_id: activeBusinessId,
            p_period_start: periodStart,
            p_period_end: periodEnd,
            p_tax_type: taxType,
          },
        );

        if (!mountedRef.current) return;
        if (result.success) {
          setData(result.data);
          setError(null);
        } else {
          setError(new Error(result.error.code));
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
        inFlightRef.current = false;
      }
    },
    [activeBusinessId, periodStart, periodEnd, taxType],
  );

  // Reset on input change.
  useEffect(() => {
    setData(null);
    setError(null);
  }, [activeBusinessId, periodStart, periodEnd, taxType]);

  // Mount fetch.
  useEffect(() => {
    if (!activeBusinessId || !periodStart || !periodEnd) return;
    void doFetch(false);
  }, [activeBusinessId, periodStart, periodEnd, taxType, doFetch]);

  // Page Visibility focus refetch.
  useEffect(() => {
    if (!activeBusinessId || !periodStart || !periodEnd) return;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFocusFetchRef.current >= FOCUS_REFETCH_THROTTLE_MS) {
        lastFocusFetchRef.current = now;
        void doFetch(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { document.removeEventListener('visibilitychange', onVisibility); };
  }, [activeBusinessId, periodStart, periodEnd, doFetch]);

  // MONEY_MUTATION_EVENT cache invalidation.
  useEffect(() => {
    const onMutation = () => {
      if (activeBusinessId && periodStart && periodEnd) void doFetch(true);
    };
    window.addEventListener(MONEY_MUTATION_EVENT, onMutation);
    return () => { window.removeEventListener(MONEY_MUTATION_EVENT, onMutation); };
  }, [activeBusinessId, periodStart, periodEnd, doFetch]);

  const refetch = useCallback(async () => { await doFetch(true); }, [doFetch]);

  return useMemo(
    () => ({ data, isLoading, isRefreshing, error, refetch }),
    [data, isLoading, isRefreshing, error, refetch],
  );
}
